import { AppDispatch, RootState } from '@/storage/store'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useTypesafeStudent } from './useTypesafeStudent'
import { useEffect, useRef } from 'react'
import { LogUserActivity } from '@/apis/users/requests'
import { LOG_TYPES } from '../constants'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { updateStudent } from '@/storage/applications/upsert-student.action'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { useAppAlert } from '@/app/AppAlertProvider'
import { Input } from '@/app/common/components/Input'
import { SubmitButton } from './shared/submit-button'
import FamilyWizardLayout from '../FamilyWizardLayout'
import useStudenDetailsDocumentParams from './hooks/useStudentDetailsDocumentParams'
import { RefreshFamilyApplication } from '@/storage/applications/familyApplicationSlice'

const studentSchema = z.object({
  document_name: z.string().min(1, 'answer_is_required'),
  document_description: z.string().min(1, 'answer_is_required'),
  agree_external_document: z.boolean(),
}).refine((value) => {
  return value.agree_external_document
}, {
  message: 'answer_is_required',
  path: ['agree_external_document']
})
type FormValues = z.infer<typeof studentSchema>

export const StudentBirthdateAdditionalWarning = () => {
  const ProgramId = useSelector(selectProgramID)
  const { t: translate } = useTranslation()
  const { fullName } = useTypesafeStudent()
  const { fireAlert } = useAppAlert()
  const dispatch = useDispatch<AppDispatch>()
  const {
    selectedStudentId,
    data: FamilyApplication,
    request: { status: updateStudentStatus },
  } = useSelector((state: RootState) => state.FamilyApplication)
  const {
    onStudentDetailFormSelect
  } = useStudenDetailsDocumentParams()

  const student = FamilyApplication?.students?.find(
    (student) => student.id === selectedStudentId
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      document_name: student?.document_name ?? '',
      document_description: student?.document_description ?? '',
      agree_external_document: Boolean(student?.agree_external_document) ?? false
    },
  })

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const resultAction = await dispatch(
      updateStudent({
        program_id: ProgramId,
        student_id: selectedStudentId ?? undefined,
        document_name: data.document_name,
        document_description: data.document_description,
        agree_external_document: Number(data.agree_external_document),
        applicant_doesnt_have_birth_cert: true,
      })
    )

    if (updateStudent.fulfilled.match(resultAction)) {
      dispatch(RefreshFamilyApplication(ProgramId))
      onStudentDetailFormSelect('relation_to_student')
    } else {

      
      fireAlert({
        variant: 'error',
        message: resultAction.payload?.message,
      })
    }
  }

  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: "Additional Detail Required"
      }) // Log page view
      hasLoggedPageView.current = true; // Mark as executed
    }
  }, [])

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('additional_detail_required')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content>
        <div className="font-medium text-gray-900">
          {translate('unavailable_bc_cases')}
        </div>

        <form
          id="additional-details-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
        >
          <section
            className="flex flex-col mt-10 space-y-2"
            aria-labelledby="document_name_label"
          >
            <label
              id="document_name_label"
              htmlFor="document_name"
              className="block text-sm font-bold leading-6 text-gray-900"
            >
              {translate('documents_being_uploaded')}{' '}
              <span className="text-danger ml-1">*</span>
            </label>
            <div>
              <Input
                id="document_name"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                {...register('document_name')}
              />
            </div>
            {errors.document_name && (
              <p className="mt-1 text-sm text-red-600">
                {translate(errors.document_name.message ?? '')}
              </p>
            )}
          </section>

          <section
            className="flex flex-col mt-10 space-y-2"
            aria-labelledby="document_description_label"
          >
            <label
              id="document_description_label"
              htmlFor="document_description"
              className="block text-sm font-bold leading-6 text-gray-900"
            >
              {translate('describe_why_bc_not_available')}{' '}
              <span className="text-danger ml-1">*</span>
            </label>
            <div>
              <Input
                id="document_description"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                {...register('document_description')}
              />
            </div>
            {errors.document_description && (
              <p className="mt-1 text-sm text-red-600">
                {translate(errors.document_description.message ?? '')}
              </p>
            )}
          </section>


          <div className="flex pt-6 items-center justify-between">
            <div className="flex items-center">
              <input
                {...register('agree_external_document')}
                id="agree_external_document"
                name="agree_external_document"
                type="checkbox"
                // onChange={rememberMeHandled}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
              />
              <label
                htmlFor="agree_external_document"
                className="ml-3 block text-sm font-medium leading-6 text-gray-900"
              >
                {translate('certify_do_not_have_bc', {student_name: fullName})}
              </label>

            </div>
          </div>

          {errors.agree_external_document && (
            <p className="mt-1 text-sm text-red-600">
              {translate(errors.agree_external_document.message ?? '')}
            </p>
          )}
        </form>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <SubmitButton
          form="additional-details-form"
          status={updateStudentStatus}
        />
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
